import React, {
    createContext,
    useContext,
    FC,
    useState,
    useCallback,
} from 'react';

// Help track why input is disabled at any given time.
export const DisableReasons = {
    USER_WAITING: 'USER_WAITING',
    DIRECTLINE_LOADING: 'DIRECTLINE_LOADING',
    GREETING_OPTIMIZATION: 'GREETING_OPTIMIZATION',
    OTHER: 'OTHER',
} as const;

export type DisableReason = keyof typeof DisableReasons;

interface DisableTextSendContext {
    disableTextSend: boolean;
    setDisableTextSend: (
        disableTextSend: boolean,
        reason?: DisableReason
    ) => void;
}

/**
 * Represents a context for disabling text sending by the user with the chat client.
 */
export const DisableTextSendContext = createContext<DisableTextSendContext>({
    disableTextSend: false,
    setDisableTextSend: () => {
        console.warn(
            'setDisableTextSend has been invoked before it was initialized'
        );
    },
});

/**
 * Represents a component that disables text sending.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} props.initialMode - The initial mode for disabling text send.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {React.ReactNode} - The rendered component.
 */
export const DisableTextSendProvider: FC<{
    initialMode: boolean;
    children: React.ReactNode;
}> = ({ initialMode, children }) => {
    // Track why input should be disabled in state.
    const [reasons, setReasons] = useState<Record<DisableReason, boolean>>({
        USER_WAITING: false,
        DIRECTLINE_LOADING: false,
        GREETING_OPTIMIZATION: false,
        OTHER: initialMode,
    });

    // Disable if any keys are set to true
    const disableTextSend = Object.values(reasons).some(Boolean);

    // Update disabled state; overall disableTextSend is derived from all reasons
    const setDisableTextSend = useCallback(
        (disable: boolean, reason: DisableReason = DisableReasons.OTHER) => {
            // Avoid unnecessary re-renders if the value for this reason didn't change.
            setReasons((prev) =>
                prev[reason] === disable ? prev : { ...prev, [reason]: disable }
            );
        },
        []
    );

    return (
        <DisableTextSendContext.Provider
            value={{ disableTextSend, setDisableTextSend }}
        >
            {children}
        </DisableTextSendContext.Provider>
    );
};

/**
 * Retrieve the value of useDisableTextSend from DisableTextSendContext
 *
 * @returns {*} The value of useDisableTextSend from DisableTextSendContext
 */
export const useDisableTextSend = () => useContext(DisableTextSendContext);
